/**
 * ExamSelector Component
 * Step 2 of the appointment scheduling wizard — select complementary exams
 *
 * @version 2025.1.0
 * @description Seletor de exames complementares para agendamento
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Science,
  Add,
  CheckCircle,
  Star,
  InfoOutlined,
  Cancel,
  HealthAndSafety,
} from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { appointmentExamService } from '../../../services/appointmentExamService';
import { occupationalExamService } from '../../../services/occupationalExamService';
import { riskApplicationService } from '../../../services/riskApplicationService';
import type { SuggestedExam } from '../../../types/appointmentExam';
import { APPLICATION_LEVEL_LABELS, APPLICATION_LEVEL_COLORS } from '../../../types/appointmentExam';
import type { OccupationalExam } from '../../../types/occupationalExam';

interface ExamSelectorProps {
  employeeId: number | null;
  companyId: number | null;
  examType: string;
  selectedExamIds: number[];
  onSelectionChange: (examIds: number[]) => void;
  onSuggestionsLoaded?: (suggestions: SuggestedExam[]) => void;
  /** Quando true (criação), os exames do PCMSO já vêm marcados automaticamente. */
  autoSelectSuggestions?: boolean;
}

/** Risco ocupacional aplicado ao funcionário (contexto na seleção de exames). */
interface EmployeeRisk {
  riskId: number;
  riskName: string;
  scope: string;
  exposureLevel: string | null;
}

export const ExamSelector: React.FC<ExamSelectorProps> = ({
  employeeId,
  companyId,
  examType,
  selectedExamIds,
  onSelectionChange,
  onSuggestionsLoaded,
  autoSelectSuggestions = false,
}) => {
  // Suggestions state
  const [suggestions, setSuggestions] = useState<SuggestedExam[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  // Espelho da seleção atual (evita closure stale no effect de sugestões)
  const selectedRef = useRef<number[]>(selectedExamIds);
  selectedRef.current = selectedExamIds;

  // Riscos ocupacionais aplicados ao funcionário (contexto — #264)
  const [risks, setRisks] = useState<EmployeeRisk[]>([]);


  // Autocomplete search state
  const [searchInput, setSearchInput] = useState('');
  const [buscaAdiada, setBuscaAdiada] = useState('');

  // Map of examId -> examName for display in the "selected" section
  const [examNameMap, setExamNameMap] = useState<Record<number, string>>({});

  // Disabled state when missing required params
  const isDisabled = employeeId === null || companyId === null;

  // Load suggestions when all params are available
  useEffect(() => {
    if (isDisabled || !examType) {
      setSuggestions([]);
      return;
    }

    const loadSuggestions = async () => {
      try {
        setLoadingSuggestions(true);
        setSuggestionsError(null);

        const data = await appointmentExamService.getSuggestions(employeeId!, examType);

        setSuggestions(data);
        onSuggestionsLoaded?.(data);

        // Pré-seleciona os exames do PCMSO (na criação), preservando os manuais (#261)
        if (autoSelectSuggestions && data.length > 0) {
          const suggestedIds = data.map((s) => s.examId);
          const merged = Array.from(new Set([...selectedRef.current, ...suggestedIds]));
          if (merged.length !== selectedRef.current.length) {
            onSelectionChange(merged);
          }
        }

        // Build name map from suggestions
        const nameMap: Record<number, string> = {};
        data.forEach((s) => {
          nameMap[s.examId] = s.examName;
        });
        setExamNameMap((prev) => ({ ...prev, ...nameMap }));

      } catch (err: unknown) {
        console.error('Error loading exam suggestions:', err);
        setSuggestionsError('Erro ao carregar sugestões de exames');
      } finally {
        setLoadingSuggestions(false);
      }
    };

    loadSuggestions();
  }, [employeeId, companyId, examType]);

  // Carrega os riscos ocupacionais aplicados ao funcionário (contexto — #264)
  useEffect(() => {
    if (!employeeId) {
      setRisks([]);
      return;
    }
    let cancelled = false;
    riskApplicationService
      .getEmployeeRisks(employeeId)
      .then((data) => { if (!cancelled) setRisks(data); })
      .catch((err: unknown) => {
        console.error('Error loading employee risks:', err);
        if (!cancelled) setRisks([]);
      });
    return () => { cancelled = true; };
  }, [employeeId]);

  // Search for exams in the catalog (debounced via input change)
  useEffect(() => {
    const timer = setTimeout(() => setBuscaAdiada(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const buscarNoCatalogo = useCallback(
    () =>
      occupationalExamService
        .getOccupationalExams({ search: buscaAdiada, status: 'ACTIVE' as any, limit: 20 })
        .then((response) => response.exams),
    [buscaAdiada]
  );

  // Sem ler o catálogo de exames ocupacionais não há como concluir o agendamento, e até aqui a
  // busca voltava vazia sem dizer nada — indistinguível de "não existe exame com esse nome".
  const catalogo = useListaPermitida<OccupationalExam>(
    'occupational_exam.read',
    'Exames ocupacionais',
    buscarNoCatalogo,
    { habilitado: buscaAdiada.length >= 2 }
  );
  const searchResults = catalogo.itens;
  const searchLoading = catalogo.carregando;

  // Resolve names for pre-selected exams not yet in examNameMap (e.g. after remount)
  useEffect(() => {
    if (selectedExamIds.length === 0 || !catalogo.permitido) return;
    const unknownIds = selectedExamIds.filter(
      (id) => !examNameMap[id] && !suggestions.find((s) => s.examId === id),
    );
    if (unknownIds.length === 0) return;

    let cancelled = false;
    Promise.all(
      unknownIds.map((id) =>
        occupationalExamService.getOccupationalExamById(id).then((exam) => ({ id, name: exam.examName })).catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      const nameMap: Record<number, string> = {};
      for (const r of results) {
        if (r) nameMap[r.id] = r.name;
      }
      if (Object.keys(nameMap).length > 0) {
        setExamNameMap((prev) => ({ ...prev, ...nameMap }));
      }
    });
    return () => { cancelled = true; };
  }, [selectedExamIds, suggestions, catalogo.permitido]);


  // Toggle a suggestion checkbox
  const handleToggleSuggestion = useCallback(
    (examId: number) => {
      const isSelected = selectedExamIds.includes(examId);
      if (isSelected) {
        onSelectionChange(selectedExamIds.filter((id) => id !== examId));
      } else {
        onSelectionChange([...selectedExamIds, examId]);
      }
    },
    [selectedExamIds, onSelectionChange],
  );

  // Add an exam from the autocomplete
  const handleAddExam = useCallback(
    (_event: React.SyntheticEvent, exam: OccupationalExam | null) => {
      if (!exam) return;
      if (selectedExamIds.includes(exam.id)) return;

      setExamNameMap((prev) => ({ ...prev, [exam.id]: exam.examName }));
      onSelectionChange([...selectedExamIds, exam.id]);
      setSearchInput('');
      setBuscaAdiada('');
    },
    [selectedExamIds, onSelectionChange],
  );

  // Remove a selected exam chip
  const handleRemoveExam = useCallback(
    (examId: number) => {
      onSelectionChange(selectedExamIds.filter((id) => id !== examId));
    },
    [selectedExamIds, onSelectionChange],
  );

  // Filter autocomplete options to exclude already-selected exams
  const filteredSearchResults = useMemo(
    () => searchResults.filter((exam) => !selectedExamIds.includes(exam.id)),
    [searchResults, selectedExamIds],
  );

  // Resolve exam name for display
  const getExamName = (examId: number): string => {
    if (examNameMap[examId]) return examNameMap[examId];
    const suggestion = suggestions.find((s) => s.examId === examId);
    if (suggestion) return suggestion.examName;
    return `Exame #${examId}`;
  };

  // --- Disabled state ---
  if (isDisabled) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Selecione empresa e funcionário primeiro
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {/* ── Info banner ── */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Exames complementares são opcionais. Selecione os exames necessários ou avance sem exames.
      </Alert>

      {/* ── Riscos Ocupacionais do funcionário (contexto) ── */}
      {risks.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HealthAndSafety fontSize="small" color="error" />
            Riscos Ocupacionais
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {risks.map((risk) => (
              <Chip
                key={risk.riskId}
                label={risk.exposureLevel ? `${risk.riskName} · ${risk.exposureLevel}` : risk.riskName}
                size="small"
                color="error"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* ── Exames do PCMSO ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Science fontSize="small" color="primary" />
          Exames do PCMSO
          <Tooltip
            title="Exames aplicados no PCMSO da empresa, setor, cargo ou funcionário escolhido. Já vêm marcados automaticamente; ajuste se necessário."
            arrow
            placement="right"
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              <InfoOutlined fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Typography>

        {loadingSuggestions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Carregando sugestões...
            </Typography>
          </Box>
        )}

        {suggestionsError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {suggestionsError}
          </Alert>
        )}

        {!loadingSuggestions && !suggestionsError && suggestions.length === 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Nenhum exame sugerido para este tipo de consulta. Você pode adicionar exames manualmente abaixo.
          </Alert>
        )}

        {!loadingSuggestions && suggestions.map((suggestion) => (
          <Box
            key={suggestion.examId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              py: 0.5,
              px: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedExamIds.includes(suggestion.examId)}
                  onChange={() => handleToggleSuggestion(suggestion.examId)}
                />
              }
              label={
                <Typography variant="body2" fontWeight="medium">
                  {suggestion.examName}
                </Typography>
              }
              sx={{ mr: 0, flex: '0 0 auto' }}
            />

            {suggestion.esocialCode && (
              <Chip
                label={suggestion.esocialCode}
                size="small"
                variant="outlined"
                color="default"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}

            {suggestion.isRequired && (
              <Chip
                icon={<Star sx={{ fontSize: 14 }} />}
                label="Obrigatório"
                size="small"
                color="warning"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}

            <Chip
              label={APPLICATION_LEVEL_LABELS[suggestion.applicationLevel] ?? suggestion.applicationLevel}
              size="small"
              color={APPLICATION_LEVEL_COLORS[suggestion.applicationLevel] ?? 'default'}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />

            {suggestion.periodicityMonths && (
              <Typography variant="caption" color="text.secondary">
                (a cada {suggestion.periodicityMonths} meses)
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ── Adicionar Outros ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Add fontSize="small" color="primary" />
          Adicionar Outros
        </Typography>

        <Autocomplete
          options={filteredSearchResults}
          getOptionLabel={(option) => {
            const code = option.esocialCode ? ` (${option.esocialCode})` : '';
            return `${option.examName}${code}`;
          }}
          loading={searchLoading}
          value={null}
          onChange={handleAddExam}
          inputValue={searchInput}
          onInputChange={(_event, value) => setSearchInput(value)}
          noOptionsText={
            catalogo.bloqueio ??
            (searchInput.length < 2
              ? 'Digite ao menos 2 caracteres para buscar'
              : 'Nenhum exame encontrado')
          }
          loadingText="Buscando exames..."
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Buscar exame por nome ou código..."
              size="small"
              helperText={catalogo.bloqueio}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{option.examName}</Typography>
                {option.esocialCode && (
                  <Typography variant="caption" color="text.secondary">
                    eSocial: {option.esocialCode}
                    {option.tussCode ? ` | TUSS: ${option.tussCode}` : ''}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          filterOptions={(x) => x}
          clearOnBlur
          blurOnSelect
          sx={{ mb: 1 }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ── Exames Selecionados ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle fontSize="small" color="success" />
          Exames Selecionados
          <Chip
            label={selectedExamIds.length}
            size="small"
            color={selectedExamIds.length > 0 ? 'primary' : 'default'}
            sx={{ ml: 0.5, height: 22, minWidth: 28 }}
          />
        </Typography>

        {selectedExamIds.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Nenhum exame selecionado
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedExamIds.map((examId) => (
              <Chip
                key={examId}
                label={getExamName(examId)}
                onDelete={() => handleRemoveExam(examId)}
                deleteIcon={<Cancel titleAccess="Remover exame" />}
                color="primary"
                variant="filled"
                size="small"
              />
            ))}
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default ExamSelector;
